import React, { createContext, useContext, ReactNode } from 'react';
import { useNotification } from 'reablocks';
import { ApolloQueryResult } from '@apollo/client';
import {
  IntegrationTagTypeConnection,
  IntegrationTypeConnection
} from 'core/graphql/graphql';

interface PollingDataType {
  integrationId: string;
  name: string;
  refetch: (variables: { id: string }) => Promise<
    ApolloQueryResult<{
      integrations: IntegrationTypeConnection;
      integrationTags: IntegrationTagTypeConnection;
    }>
  >;
  initialLastSuccessfulConnectivityTest: string | null | undefined;
  maxPolls?: number;
  pollInterval?: number;
}

interface IntegrationPollingContextType {
  startPolling: (options: PollingDataType) => void;
}

const IntegrationPollingContext = createContext<
  IntegrationPollingContextType | undefined
>(undefined);

export const useIntegrationPolling = () => {
  const context = useContext(IntegrationPollingContext);
  if (!context) {
    throw new Error(
      'useIntegrationPolling must be used within an IntegrationPollingProvider'
    );
  }
  return context;
};

interface IntegrationPollingProviderProps {
  children: ReactNode;
}

export const IntegrationPollingProvider: React.FC<
  IntegrationPollingProviderProps
> = ({ children }) => {
  const { notifySuccess, notifyError } = useNotification();

  const startIntegrationPolling = ({
    integrationId,
    name,
    refetch,
    initialLastSuccessfulConnectivityTest,
    maxPolls = 20, // default max 20 attempts
    pollInterval = 10000 // default poll interval 10 seconds
  }: PollingDataType) => {
    let currentPollCount = 0;

    const poll = async () => {
      try {
        currentPollCount++;

        // Use the existing refetch function with proper variables format
        const result = await refetch({ id: integrationId });

        // Get the current lastSuccessfulConnectivityTest from the result
        const currentLastSuccessfulConnectivityTest =
          result.data?.integrations?.edges?.[0]?.node
            ?.lastSuccessfulConnectivityTest;

        // Stop polling if lastSuccessfulConnectivityTest has changed
        if (
          currentLastSuccessfulConnectivityTest !==
          initialLastSuccessfulConnectivityTest
        ) {
          notifySuccess(`Successfully connected to ${name}!`);
          return;
        } else if (currentPollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          notifyError(`Failed to connect to ${name}`);
          return;
        }
      } catch (error) {
        notifyError(`Failed to connect to ${name}`);
      }
    };

    setTimeout(poll, pollInterval);
  };

  const value: IntegrationPollingContextType = {
    startPolling: startIntegrationPolling
  };

  return (
    <IntegrationPollingContext.Provider value={value}>
      {children}
    </IntegrationPollingContext.Provider>
  );
};
